import type { SeatHand } from '../../types/conceptualProblems';

/**
 * Splits a conceptual problem's hands into the two the table frame has room for (feature 029).
 *
 * <p>The first goes in the frame's hand panel and the second in its `secondaryHand` slot — the
 * arrangement feature 025 already uses for partner's hand. No problem in the data records more
 * than two (measured, research D4), so a third column is a shape the data has never asked for.</p>
 *
 * <p>The server already orders them deciding-seat first; this does not rely on that. An editor
 * wants the seat being ASKED in the panel their eye reaches first, whatever order the JSON
 * happened to list, and a sort here is cheaper than a trust boundary.</p>
 *
 * <p>It lives in its own module rather than beside the components that use it so that file exports
 * components alone — the fast-refresh rule the whole client is held to.</p>
 */
/** A seat the compass layout can place (CompassBoardLayout). */
export type CompassSeat = 'N' | 'E' | 'S' | 'W';

/** Which way a two-hand compass runs: North/South, East/West, or one of each. */
export type CompassAxis = 'ns' | 'ew' | 'mixed';

export function compassAxis(seats: CompassSeat[]): CompassAxis {
  const vertical = seats.some((s) => s === 'N' || s === 'S');
  const horizontal = seats.some((s) => s === 'E' || s === 'W');
  if (vertical && !horizontal) return 'ns';
  if (horizontal && !vertical) return 'ew';
  return 'mixed';
}

/**
 * `grid-template-areas` for a two-hand compass (CompassBoardLayout; spec 031, client 2026-10-05):
 * North over the middle over South, or West, the middle and East in a row. The middle `b` is the
 * board, or the partition line where there is none, so it is always there. '.' is an empty cell.
 */
export function compassAreas(seats: CompassSeat[]): string {
  const axis = compassAxis(seats);
  if (axis === 'ns') return `"n" "b" "s"`;
  if (axis === 'ew') return `"w b e"`;
  return `". n ." "w b e" ". s ."`;
}

export function splitHands(hands: SeatHand[]): { first: SeatHand | null; second: SeatHand | null } {
  const ordered = [...hands].sort((a, b) => Number(b.isDecisionSeat) - Number(a.isDecisionSeat));
  return { first: ordered[0] ?? null, second: ordered[1] ?? null };
}
